import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { cargarLogoAmzedel, NOMBRE_FARMACIA, SISTEMA_NOMBRE } from '@/lib/marca';
import { dibujarEncabezado, COLOR_DARK, COLOR_PRIMARY, COLOR_SOFT, COLOR_LINE } from '@/lib/pdf/encabezado';
import type { EmpleadoResumen, Periodo, PeriodoRango, VentaRow } from '@/lib/ventas/types';
import { nombreArchivoEmpleado, nombreArchivoPeriodo, rolEtiqueta } from '@/lib/ventas/periodo';

type Doc = jsPDF & {
  autoTable: (options: Record<string, unknown>) => void;
  lastAutoTable?: { finalY: number };
};

function lastY(doc: Doc): number {
  return doc.lastAutoTable?.finalY ?? 0;
}

function fechaCorta(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function horaCorta(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function fmtFecha(f: Date): string {
  return f.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtMonto(n: number): string {
  return `Bs. ${Number(n || 0).toFixed(2)}`;
}

function filasVentas(ventas: VentaRow[]): string[][] {
  return ventas.map((v) => [
    fechaCorta(v.fecha),
    horaCorta(v.fecha),
    v.nombre_nit || 'Consumidor Final',
    v.numero_nit || '—',
    v.metodo_pago,
    fmtMonto(Number(v.total)),
  ]);
}

function escribirPie(doc: Doc): void {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(150, 156, 153);
    doc.text(
      `${NOMBRE_FARMACIA} — Documento generado automáticamente por ${SISTEMA_NOMBRE}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' },
    );
    doc.text(`Página ${i} de ${total}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
  }
}

function cajaTotal(doc: Doc, y: number, etiqueta: string, cantidad: number, total: number): number {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.setFillColor(COLOR_SOFT[0], COLOR_SOFT[1], COLOR_SOFT[2]);
  doc.roundedRect(14, y, pageWidth - 28, 20, 2, 2, 'F');
  doc.setDrawColor(COLOR_LINE[0], COLOR_LINE[1], COLOR_LINE[2]);
  doc.roundedRect(14, y, pageWidth - 28, 20, 2, 2, 'S');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(COLOR_DARK[0], COLOR_DARK[1], COLOR_DARK[2]);
  doc.text(etiqueta, 20, y + 8);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(90, 96, 94);
  doc.text(`Cantidad de ventas: ${cantidad}`, 20, y + 15);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(COLOR_DARK[0], COLOR_DARK[1], COLOR_DARK[2]);
  doc.text(fmtMonto(total), pageWidth - 20, y + 13, { align: 'right' });

  return y + 28;
}

/**
 * PDF general del período: todas las ventas + total general + resumen por empleado.
 */
export async function generarPdfHistorialGeneral(
  periodo: Periodo,
  rango: PeriodoRango,
  ventas: VentaRow[],
  resumen: EmpleadoResumen[],
  generadoPor: string,
): Promise<void> {
  const doc = new jsPDF('p', 'mm', 'a4') as Doc;
  const logo = await cargarLogoAmzedel();
  const ahora = new Date();

  dibujarEncabezado(doc, logo, 'Historial de Ventas', [
    `Generado: ${fmtFecha(ahora)} ${horaCorta(ahora.toISOString())}`,
    `Por: ${generadoPor}`,
  ]);

  let y = 46;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLOR_DARK[0], COLOR_DARK[1], COLOR_DARK[2]);
  doc.setFontSize(15);
  doc.text('Historial de Ventas', 14, y);
  y += 6.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(100, 106, 103);
  doc.text(`Período: ${rango.label} (${fmtFecha(rango.inicio)} — ${fmtFecha(rango.fin)})`, 14, y);
  y += 5;

  const totalGeneral = ventas.reduce((s, v) => s + (Number(v.total) || 0), 0);

  doc.autoTable({
    startY: y,
    head: [['Fecha', 'Hora', 'Cliente', 'NIT/CI', 'Método', 'Total']],
    body: filasVentas(ventas),
    theme: 'grid',
    headStyles: { fillColor: COLOR_DARK, textColor: [255, 255, 255], fontSize: 8.5, halign: 'center', fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: [55, 60, 58] },
    alternateRowStyles: { fillColor: [246, 249, 248] },
    columnStyles: {
      0: { cellWidth: 23 },
      1: { cellWidth: 16, halign: 'center' },
      2: { cellWidth: 44 },
      3: { cellWidth: 30 },
      4: { cellWidth: 23, halign: 'center' },
      5: { cellWidth: 28, halign: 'right' },
    },
    styles: { cellPadding: 2.6, lineColor: COLOR_LINE, lineWidth: 0.1 },
    margin: { left: 14, right: 14 },
  });

  let endY = lastY(doc) + 8;
  if (endY > 240) {
    doc.addPage();
    endY = 20;
  }
  endY = cajaTotal(doc, endY, 'TOTAL GENERAL', ventas.length, totalGeneral);

  if (endY > 232) {
    doc.addPage();
    endY = 20;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(COLOR_DARK[0], COLOR_DARK[1], COLOR_DARK[2]);
  doc.text('Resumen por Empleado', 14, endY);
  endY += 3;

  doc.autoTable({
    startY: endY,
    head: [['Empleado', 'Usuario', 'Rol', 'Ventas', 'Efectivo', 'QR', 'Total']],
    body: resumen.map((e) => [
      e.nombre,
      e.usuario,
      rolEtiqueta(e.rol),
      String(e.cantidad),
      fmtMonto(e.efectivo),
      fmtMonto(e.qr),
      fmtMonto(e.total),
    ]),
    foot: [['TOTAL', '', '', String(ventas.length), '', '', fmtMonto(totalGeneral)]],
    theme: 'grid',
    headStyles: { fillColor: COLOR_PRIMARY, textColor: [255, 255, 255], fontSize: 8.5, fontStyle: 'bold' },
    footStyles: { fillColor: COLOR_SOFT, textColor: COLOR_DARK, fontSize: 8.5, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: [55, 60, 58] },
    columnStyles: {
      3: { halign: 'center' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
    },
    styles: { cellPadding: 2.6, lineColor: COLOR_LINE, lineWidth: 0.1 },
    margin: { left: 14, right: 14 },
  });

  escribirPie(doc);
  doc.save(nombreArchivoPeriodo(periodo, ahora));
}

/**
 * PDF individual por empleado: encabezado, datos del empleado, ventas y resumen.
 */
export async function generarPdfHistorialEmpleado(
  empleado: EmpleadoResumen,
  periodo: Periodo,
  rango: PeriodoRango,
  ventas: VentaRow[],
  generadoPor: string,
): Promise<void> {
  const doc = new jsPDF('p', 'mm', 'a4') as Doc;
  const logo = await cargarLogoAmzedel();
  const ahora = new Date();

  dibujarEncabezado(doc, logo, 'Historial de Ventas', [
    `Generado: ${fmtFecha(ahora)} ${horaCorta(ahora.toISOString())}`,
    `Por: ${generadoPor}`,
  ]);

  let y = 46;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(COLOR_DARK[0], COLOR_DARK[1], COLOR_DARK[2]);
  doc.setFontSize(15);
  doc.text('Historial de Ventas', 14, y);
  y += 8;

  doc.setFillColor(COLOR_SOFT[0], COLOR_SOFT[1], COLOR_SOFT[2]);
  doc.roundedRect(14, y - 4, 182, 30, 2, 2, 'F');
  doc.setDrawColor(COLOR_LINE[0], COLOR_LINE[1], COLOR_LINE[2]);
  doc.roundedRect(14, y - 4, 182, 30, 2, 2, 'S');

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(120, 126, 123);
  doc.text('Empleado', 20, y + 3);
  doc.text('Usuario', 105, y + 3);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(COLOR_DARK[0], COLOR_DARK[1], COLOR_DARK[2]);
  doc.text(empleado.nombre || '—', 20, y + 9);
  doc.text(empleado.usuario || '—', 105, y + 9);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(120, 126, 123);
  doc.text('Rol', 20, y + 17);
  doc.text('Período', 105, y + 17);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(COLOR_DARK[0], COLOR_DARK[1], COLOR_DARK[2]);
  doc.text(rolEtiqueta(empleado.rol), 20, y + 23);
  doc.text(`${rango.label} (${fmtFecha(rango.inicio)} — ${fmtFecha(rango.fin)})`, 105, y + 23);

  y += 34;

  doc.autoTable({
    startY: y,
    head: [['Fecha', 'Hora', 'Cliente', 'NIT/CI', 'Método', 'Total']],
    body: filasVentas(ventas),
    theme: 'grid',
    headStyles: { fillColor: COLOR_DARK, textColor: [255, 255, 255], fontSize: 8.5, halign: 'center', fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: [55, 60, 58] },
    alternateRowStyles: { fillColor: [246, 249, 248] },
    columnStyles: {
      0: { cellWidth: 23 },
      1: { cellWidth: 16, halign: 'center' },
      2: { cellWidth: 44 },
      3: { cellWidth: 30 },
      4: { cellWidth: 23, halign: 'center' },
      5: { cellWidth: 28, halign: 'right' },
    },
    styles: { cellPadding: 2.6, lineColor: COLOR_LINE, lineWidth: 0.1 },
    margin: { left: 14, right: 14 },
  });

  let endY = lastY(doc) + 9;
  if (endY > 228) {
    doc.addPage();
    endY = 20;
  }

  const total = ventas.reduce((s, v) => s + (Number(v.total) || 0), 0);
  const efectivo = ventas.filter((v) => v.metodo_pago === 'Efectivo').reduce((s, v) => s + (Number(v.total) || 0), 0);
  const qr = ventas.filter((v) => v.metodo_pago === 'QR').reduce((s, v) => s + (Number(v.total) || 0), 0);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(COLOR_DARK[0], COLOR_DARK[1], COLOR_DARK[2]);
  doc.text('Resumen', 14, endY);
  endY += 3;

  doc.autoTable({
    startY: endY,
    head: [['Concepto', 'Detalle']],
    body: [
      ['Cantidad de ventas', String(ventas.length)],
      ['Total vendido', fmtMonto(total)],
      ['Efectivo', fmtMonto(efectivo)],
      ['QR', fmtMonto(qr)],
    ],
    theme: 'grid',
    headStyles: { fillColor: COLOR_PRIMARY, textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, textColor: [55, 60, 58] },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 90 },
      1: { halign: 'right' },
    },
    styles: { cellPadding: 3, lineColor: COLOR_LINE, lineWidth: 0.1 },
    margin: { left: 14, right: 14 },
  });

  escribirPie(doc);
  doc.save(nombreArchivoEmpleado(empleado.usuario || empleado.nombre, periodo));
}